import type Button from '@naturehouse/design-system/components/atoms/button/Button';
import { NotificationType } from '@naturehouse/design-system/components/atoms/notification/Notification';
import VerificationCollection from '../../../collections/VerificationCollection';
import createNotification from '../../../util/notification';
import AccountVerificationButtonManager from './AccountVerificationButtonManager';
import AccountVerificationCooldownManager from './AccountVerificationCooldownManager';

export default class AccountVerification {
    static #instance: AccountVerification | null = null;

    #requestInFlight = false;

    readonly #buttonManager = new AccountVerificationButtonManager();

    readonly #cooldownManager: AccountVerificationCooldownManager;

    private constructor() {
        this.#cooldownManager = new AccountVerificationCooldownManager(
            () => this.#syncButtonState(true),
            () => this.#syncButtonState(false)
        );
        this.#cooldownManager.restore().catch(() => undefined);
    }

    public static getInstance(): AccountVerification {
        if (!AccountVerification.#instance) {
            AccountVerification.#instance = new AccountVerification();
        }

        AccountVerification.#instance.syncButtons();

        return AccountVerification.#instance;
    }

    public static destroyInstance(): void {
        if (AccountVerification.#instance === null) {
            return;
        }

        AccountVerification.#instance.#cooldownManager.destroy();
        AccountVerification.#instance = null;
    }

    public syncButtons(): void {
        this.#buttonManager.bindButtons((action, button) =>
            this.sendVerificationEmail(action, button)
        );
        this.#syncButtonState(this.#cooldownManager.isOnCooldown());
    }

    public async sendVerificationEmail(action: string, trigger?: HTMLElement): Promise<void> {
        if (this.#requestInFlight || this.#cooldownManager.isOnCooldown()) {
            return;
        }

        this.#requestInFlight = true;

        if (trigger && 'disabled' in trigger) {
            (trigger as Button).disabled = true;
        }

        try {
            const message = await VerificationCollection.getInstance().add({}, { action });

            if (message) {
                createNotification(NotificationType.SUCCESS, message);
            }

            this.#cooldownManager.recordSuccess();
        } catch (error) {
            if (error instanceof Error && error.message) {
                createNotification(NotificationType.ERROR, error.message);
            }

            this.#syncButtonState(this.#cooldownManager.isOnCooldown());
        } finally {
            this.#requestInFlight = false;
        }
    }

    #syncButtonState(disabled: boolean): void {
        this.#buttonManager.applyState({
            disabled,
            onCooldown: this.#cooldownManager.isOnCooldown(),
            remainingSeconds: this.#cooldownManager.getRemainingSeconds()
        });
    }
}
